import { create } from "zustand";
import type {
  FieldState,
  LineItemRow,
  Viewport,
  VerificationState,
  FieldValidationResult,
  RowCheckResultEntry,
} from "./types";
import { resolveEffectiveThreshold, hasSuspectSpan, preferredBatchLabel } from "@capture/shared";
import { computeInitialFieldIndex } from "../hooks/useSmartSkipIterator";
import { api, ApiError } from "@/lib/api-client";

// ─── Phase 20/C3b — Line-item persistence ─────────────────────────────────
// Store actions update local state optimistically; these helpers persist to
// the API so refresh doesn't lose operator edits. 401s bounce to Suite SSO
// via api-client. Other errors log to console — operators see the local
// state continue to render so a transient failure doesn't strand the grid.

// F-063 — each pending entry keeps its `send` thunk alongside the debounce
// timer so flushPendingWrites can fire it immediately instead of waiting
// out the window. `inFlightKeyedWrites` tracks PATCHes whose timer already
// fired but whose response hasn't settled — flush awaits those too.
interface PendingKeyedWrite {
  handle: ReturnType<typeof setTimeout>;
  send: () => Promise<unknown>;
  /** Re-schedule this write — used when a flush-fired send fails, so the value isn't dropped. */
  rearm: () => void;
}

// Immutable constants + pure helpers stay at module scope (shared by every
// store instance — nothing mutable lives here anymore).
function swallowPersistError(label: string) {
  return (e: unknown) => {
    if (e instanceof ApiError && e.status === 401) return; // 401 bounces inside api-client
    console.warn(`${label} persist failed`, e);
  };
}

const CELL_DEBOUNCE_MS = 400;

// F24 (2026-05-12) — Document-field persistence mirror of the line-item pattern.
// setFieldValue used to be pure local state; operator edits were lost on
// refresh/navigation because nothing PATCHed the keyedValue. Now every
// setFieldValue schedules a debounced PATCH to
// `PATCH /api/documents/:docId/fields/:fieldId` with `{ value }`.
const FIELD_DEBOUNCE_MS = 400;

const DEFAULT_VIEWPORT: Viewport = { x: 0, y: 0, scale: 1, rotation: 0 };

/**
 * Can the operator actually key this field? Read-only (derived) and
 * skip-policy fields are never presented for keying, so nothing may block
 * progress on them. Shared by `advanceField`'s needsEdit walk and the
 * submit gate's required-blank check (`runValidatedSubmit`, F-095) so the
 * two "which fields block progress" predicates can't drift.
 */
export function isOperatorKeyable(
  f: Pick<FieldState, "readOnly" | "verificationPolicy">,
): boolean {
  return !f.readOnly && f.verificationPolicy !== "skip";
}

/**
 * Handwriting Phase 3 — which engine's candidate the persisted field value
 * belongs to when the operator hasn't toggled yet. A "fused" engine never
 * carries candidates, so only "tesseract" | "trocr" can reach consumers.
 * Shared by the swap action AND the FieldInput indicator so the visible
 * active-engine marker and the swap behavior can never disagree.
 */
export function defaultCandidateEngine(candidates: { engine: string }): "tesseract" | "trocr" {
  return candidates.engine === "trocr" ? "trocr" : "tesseract";
}

/**
 * SP3 §4 — factory that produces a FRESH verification store, each with its
 * OWN pending-write maps, in-flight set, and flush. The standalone VS consumes
 * the DEFAULT instance (`useVerificationStore` below) so single-doc behavior is
 * byte-identical; the future multi-pane station mounts one instance per pane
 * via `VerificationStoreProvider`.
 */
export function createVerificationStore() {
  // ─── Per-instance persistence state ──────────────────────────────────────
  // Was module-global — moved here so each pane's store owns its own debounce
  // timers / in-flight set and never crosses writes with a sibling pane.
  const inFlightKeyedWrites = new Set<Promise<unknown>>();

  function trackInFlight<T>(p: Promise<T>): Promise<T> {
    inFlightKeyedWrites.add(p);
    const drop = () => inFlightKeyedWrites.delete(p);
    p.then(drop, drop);
    return p;
  }

  const pendingCellWrites = new Map<string, PendingKeyedWrite>();
  const pendingFieldWrites = new Map<string, PendingKeyedWrite>();

  // Shared body of both keyed-write schedulers: (re)debounce, and on fire move
  // the write from "pending" to "in flight" via the trackInFlight'd send — that
  // registration is what lets flushPendingWrites await a write whose timer
  // already fired.
  function scheduleKeyedWrite(
    map: Map<string, PendingKeyedWrite>,
    key: string,
    debounceMs: number,
    url: string,
    value: string,
    label: string,
  ): void {
    const existing = map.get(key);
    if (existing) clearTimeout(existing.handle);
    const send = () => trackInFlight(api.patch(url, { value }));
    const handle = setTimeout(() => {
      map.delete(key);
      send().catch(swallowPersistError(label));
    }, debounceMs);
    map.set(key, {
      handle,
      send,
      rearm: () => scheduleKeyedWrite(map, key, debounceMs, url, value, label),
    });
  }

  function schedulePersistFieldValue(
    docId: string,
    fieldId: string,
    value: string,
  ): void {
    scheduleKeyedWrite(
      pendingFieldWrites,
      fieldId,
      FIELD_DEBOUNCE_MS,
      `/documents/${docId}/fields/${fieldId}`,
      value,
      "field-value",
    );
  }

  function schedulePersistLineItemCell(
    docId: string,
    lineItemId: string,
    columnName: string,
    value: string,
  ): void {
    scheduleKeyedWrite(
      pendingCellWrites,
      `${lineItemId}::${columnName}`,
      CELL_DEBOUNCE_MS,
      `/documents/${docId}/line-items/${lineItemId}/fields/${columnName}`,
      value,
      "line-item cell",
    );
  }

  /**
   * F-063 — fire every pending debounced keyed-value write NOW and resolve once
   * they (and any already-in-flight writes) settle. The document-disposal POSTs
   * (accept/reject/hold/escalate) drive this so they can't race a persist still
   * sitting in its debounce window — that race shipped an EMPTY value into a
   * real export artifact (doc cmr2aw0d…). Rejects when a flushed PATCH rejects
   * — the disposal must not proceed on a failed persist — and re-arms the
   * failed write so the error-toast Retry genuinely re-sends it instead of
   * vacuously succeeding against the stale DB value.
   */
  function flushPendingWrites(): Promise<void> {
    const flushed: Promise<unknown>[] = [];
    for (const map of [pendingFieldWrites, pendingCellWrites]) {
      for (const [key, { handle, send, rearm }] of map.entries()) {
        clearTimeout(handle);
        map.delete(key);
        flushed.push(
          send().catch((e) => {
            // Don't clobber a newer value the operator typed after this flush.
            if (!map.has(key)) rearm();
            throw e;
          }),
        );
      }
    }
    return Promise.all([...flushed, ...inFlightKeyedWrites]).then(() => undefined);
  }

  return create<VerificationState>((set, get) => {
    async function persistAddLineItemRow(
      docId: string,
      lineItemDefId: string,
      optimisticRowId: string,
    ): Promise<void> {
      try {
        const created = await api.post<{ id: string; rowNumber: number }>(
          `/documents/${docId}/line-items`,
          { lineItemDefId },
        );
        // Reconcile the optimistic id with the server id so subsequent cell
        // edits target the real row. SP3 §4 — uses this instance's `set` so a
        // non-default store reconciles its OWN optimistic rows.
        set((state) => {
          const rows = state.lineItems[lineItemDefId] ?? [];
          return {
            lineItems: {
              ...state.lineItems,
              [lineItemDefId]: rows.map((r) =>
                r.id === optimisticRowId
                  ? { ...r, id: created.id, rowNumber: created.rowNumber }
                  : r,
              ),
            },
          };
        });
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return;
        console.warn("line-item add persist failed", e);
        // Roll back the optimistic row on persistent failure.
        set((state) => {
          const rows = state.lineItems[lineItemDefId] ?? [];
          return {
            lineItems: {
              ...state.lineItems,
              [lineItemDefId]: rows.filter((r) => r.id !== optimisticRowId),
            },
          };
        });
      }
    }

    async function persistRemoveLineItemRow(docId: string, lineItemId: string): Promise<void> {
      try {
        await api.delete(`/documents/${docId}/line-items/${lineItemId}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return;
        console.warn("line-item remove persist failed", e);
      }
    }

    return {
      // Initial state
      documentId: null,
      displayId: null,
      customDisplayLabel: null,
      batchId: null,
      batchDisplayId: null,
      batchCustomDisplayLabel: null,
      jobTypeId: null,
      batchName: "",
      filename: "",
      classification: null,
      docIndex: 0,
      totalDocs: 0,
      currentStep: "",
      pages: [],
      currentPageIndex: 0,
      fields: [],
      activeFieldIndex: 0,
      fieldFocusRequest: null,
      groups: [],
      lineItems: {},
      lineItemDefinitions: [],
      validationResults: {},
      rowCheckResults: {},
      focusRowRequest: null,
      acknowledgedWarnings: new Set<string>(),
      pendingWarnAck: null,
      viewport: DEFAULT_VIEWPORT,
      savedViewerZoom: null,
      fitToWidthRequest: 0,
      // E-049 — default to fit-page (whole page visible on doc load); an
      // operator's explicit Fit Width / manual zoom persists (R-198/R-149)
      // and wins thereafter.
      fitMode: "page",
      manualZoomActive: false,
      vsAutoCenterOnFocus: true,
      focusMode: false,
      isLoading: true,
      // E-042/E-045 — default to auto-fit (0) so the snippet lands sized to
      // the zone without a manual cycle. See the [1, 2, 3, 0] cycle below.
      snippetZoom: 0,
      snippetVisible: true,
      sessionTimerVisible: true,
      jobTypeThresholds: null,
      verifyThreshold: null,
      // F-046 — per-step re-verification policy; "standard" = today's
      // confidence-driven attention model.
      stepReverifyPolicy: "standard",
      terminalMode: false,
      audioEnabled: false,
      audioVolume: 0.3,
      showBboxOverlays: true,
      showBboxLegend: false,
      verifyAutoCorrections: true,
      formReadyToSubmit: false,
      isRevalidating: false,
      sessionStats: { documentsKeyed: 0, fieldsKeyed: 0, startTime: 0, errorsFixed: 0 },
      activeEngineByFieldId: {},

      // SP3 §4 — per-instance flush (closes over this store's pending maps).
      flushPendingWrites,

      loadDocument: (doc) =>
        set((state) => {
          // Auto-activate focus mode when >70% of fields are auto-verified
          const totalFields = doc.fields.length;
          const autoVerifiedCount = doc.fields.filter(
            (f) => {
              // always-verify fields are never auto-verified
              if (f.verificationPolicy === "always-verify") return false;
              // skip fields are always counted as auto-verified
              if (f.verificationPolicy === "skip") return true;
              // R-32 P2: per-field → per-JT → 0.95 default
              const effectiveThreshold = resolveEffectiveThreshold(f.verifyThreshold, state.verifyThreshold);
              return f.ocrConfidence !== null &&
                f.ocrConfidence >= effectiveThreshold &&
                !f.isDirty &&
                f.validationStatus !== "fail";
            },
          ).length;
          const autoFocus =
            totalFields > 0 && autoVerifiedCount / totalFields > 0.7;

          return {
            documentId: doc.id,
            // R-167 WU-7 — normalize missing identity fields to null so the
            // VS header logic can treat absent-vs-null identically.
            displayId: doc.displayId ?? null,
            customDisplayLabel: doc.customDisplayLabel ?? null,
            batchId: doc.batchId,
            batchDisplayId: doc.batchDisplayId ?? null,
            batchCustomDisplayLabel: doc.batchCustomDisplayLabel ?? null,
            jobTypeId: doc.jobTypeId,
            batchName: doc.batchName,
            filename: doc.filename,
            classification: doc.classification,
            docIndex: doc.docIndex,
            totalDocs: doc.totalDocs,
            currentStep: doc.currentStep,
            // F-046 — the step's re-verification policy rides the doc payload;
            // absent (fixtures, stale prefetch cache) normalizes to standard.
            stepReverifyPolicy: doc.stepReverifyPolicy ?? "standard",
            pages: doc.pages,
            currentPageIndex: 0,
            fields: doc.fields,
            // F-023 — warning acknowledgments are per-doc.
            acknowledgedWarnings: new Set<string>(),
            pendingWarnAck: null,
            // F-088 — initial focus lands on the first smart-skip visit-set field
            // (was hard-coded 0, parking the operator on an auto-verified field).
            // Resolved against the store's current JT verifyThreshold — correct
            // for same-JT continuous feed; a JT switch re-derives once the new
            // threshold lands (see setVerifyThreshold). Empty visit set → 0, and
            // formReadyToSubmit stays false (no auto-submit on load).
            activeFieldIndex: computeInitialFieldIndex(
              doc.fields,
              state.verifyThreshold,
              doc.stepReverifyPolicy ?? "standard",
            ),
            groups: doc.groups,
            lineItems: doc.lineItems,
            lineItemDefinitions: doc.lineItemDefinitions ?? [],
            validationResults: {},
            rowCheckResults: {},
            focusRowRequest: null,
            // Handwriting Phase 3 — engine toggles are per-document state.
            activeEngineByFieldId: {},
            viewport: DEFAULT_VIEWPORT,
            // R-199 — each fresh doc starts in auto-fit; the prior doc's
            // manual-zoom override must not leak across the transition.
            manualZoomActive: false,
            isLoading: false,
            formReadyToSubmit: false,
            focusMode: autoFocus,
            sessionStats: state.sessionStats.startTime === 0
              ? { ...state.sessionStats, startTime: Date.now() }
              : state.sessionStats,
          };
        }),

      setFieldValue: (fieldId, value) =>
        set((state) => {
          // F24: debounced persist so the typed value lands as
          // DocumentField.keyedValue. Without this, accept/refresh/navigate all
          // drop the operator's work.
          if (state.documentId) {
            schedulePersistFieldValue(state.documentId, fieldId, value);
          }
          return {
            fields: state.fields.map((f) =>
              f.id === fieldId ? { ...f, value, isDirty: true } : f,
            ),
          };
        }),

      swapEngineCandidate: () =>
        set((state) => {
          const field = state.fields[state.activeFieldIndex];
          const candidates = field?.handwritingCandidates;
          // Gate-off / agreement / pre-WU field ⇒ strict no-op.
          if (!field || !candidates) return {};

          // Absent map entry = the diag's winning engine (the persisted value).
          const current = state.activeEngineByFieldId[field.id] ?? defaultCandidateEngine(candidates);
          const next = current === "tesseract" ? "trocr" : "tesseract";
          const value = next === "tesseract" ? candidates.tesseract : candidates.trocr;

          // Persist exactly like a keyed edit — the swap IS the operator's keying.
          if (state.documentId) {
            schedulePersistFieldValue(state.documentId, field.id, value);
          }
          return {
            activeEngineByFieldId: { ...state.activeEngineByFieldId, [field.id]: next },
            fields: state.fields.map((f) =>
              f.id === field.id ? { ...f, value, isDirty: true } : f,
            ),
          };
        }),

      jumpToNextSuspect: () =>
        set((state) => {
          const { fields, activeFieldIndex } = state;
          const len = fields.length;
          if (len === 0) return {};
          for (let offset = 1; offset <= len; offset++) {
            const i = (activeFieldIndex + offset) % len;
            if (hasSuspectSpan(fields[i]?.charConfidences?.spans)) {
              return { activeFieldIndex: i };
            }
          }
          return {};
        }),

      setValidationResult: (fieldId, result) =>
        set((state) => ({
          validationResults: { ...state.validationResults, [fieldId]: result },
          fields: state.fields.map((f) =>
            f.id === fieldId
              ? {
                  ...f,
                  // F-023 — warnings are a distinct, VISIBLE status: they never
                  // gate accept, but they must inform (and require a one-time
                  // acknowledgment at accept — see runValidatedSubmit).
                  validationStatus: result.status === "warning" ? "warn" : result.status,
                  validationMessage: result.message,
                  needsAttention:
                    result.status === "fail" ||
                    result.status === "warning" ||
                    (f.required && f.value === "") ||
                    (f.ocrConfidence !== null && f.ocrConfidence < 0.7 && !f.isDirty),
                }
              : f,
          ),
        })),

      advanceField: (direction) =>
        set((state) => {
          const { fields, activeFieldIndex, focusMode, verifyThreshold, verifyAutoCorrections, stepReverifyPolicy } = state;
          const len = fields.length;
          if (len === 0) return {};

          const step = direction === "next" ? 1 : -1;

          // R-144 — mark the field we're leaving as operator-acknowledged when
          // Tab/Enter advances away from it. Empty optional fields and
          // low-confidence OCR-populated fields no longer keep nagging the
          // operator once they've explicitly tabbed past. Validation failures
          // are not acknowledged: the field still needs the actual fix.
          const currentField = fields[activeFieldIndex];
          const shouldAcknowledge =
            currentField !== undefined &&
            currentField.validationStatus !== "fail" &&
            !(currentField.required && !currentField.value) &&
            !currentField.isAcknowledged;
          const fieldsWithAck = shouldAcknowledge
            ? fields.map((f, i) => (i === activeFieldIndex ? { ...f, isAcknowledged: true } : f))
            : fields;

          // Helper: does this field still need operator attention?
          function needsEdit(f: FieldState): boolean {
            if (!isOperatorKeyable(f)) return false;

            // Field has been keyed OR explicitly acknowledged, with no failing
            // rule — it's done. (R-144 widened the keyed check to include
            // isAcknowledged so Tab/Enter past an empty optional or
            // low-confidence OCR field counts as resolved.)
            if ((f.isDirty || f.isAcknowledged) && f.validationStatus !== "fail" && !(f.required && !f.value)) {
              return false;
            }

            // Validation is failing — needs attention regardless
            if (f.validationStatus === "fail") return true;

            // Required but empty — needs attention
            if (f.required && !f.value) return true;

            // Per-field always-verify beats the step policy below — it is the
            // targeted per-field knob (F-046's documented workaround) and must
            // stay potent under a narrowed step policy.
            if (f.verificationPolicy === "always-verify" && !f.isDirty) return true;

            // F-046 — per-step re-verification policy. Failures and
            // required-empty already returned true above, so these exits can
            // only resolve CLEAN fields. "standard" (default) falls through
            // to today's confidence-driven checks unchanged.
            if (stepReverifyPolicy === "only_failures") return false;
            if (stepReverifyPolicy === "only_unverified" && f.isVerified === true) return false;

            if (f.autoCorrected && verifyAutoCorrections) return true;
            if (focusMode && !f.needsAttention) return false;

            // R-32 P2: per-field → per-JT → 0.95 default
            const effectiveThreshold = resolveEffectiveThreshold(f.verifyThreshold, verifyThreshold);

            // validationStatus === "fail" already returned true above, so
            // it can't be "fail" here (the prior check is the fail guard).
            const isAutoVerified =
              f.ocrConfidence !== null &&
              f.ocrConfidence >= effectiveThreshold &&
              !(f.required && f.value === "");

            return !isAutoVerified;
          }

          // Check total form state: are there ANY fields that still need edit?
          // Uses fieldsWithAck so the just-acknowledged current field counts
          // as "done" for the form-ready check.
          const needsEditCount = fieldsWithAck.filter((f) => needsEdit(f)).length;

          if (needsEditCount === 0 && direction === "next") {
            // No fields need edit — signal form ready to submit
            return { fields: fieldsWithAck, formReadyToSubmit: true };
          }

          // Walk forward/backward
          // Forward: skip to next needs-edit field
          // Backward: go to any field (let operator revisit completed fields)
          let nextIndex = activeFieldIndex;
          for (let i = 0; i < len; i++) {
            nextIndex = nextIndex + step;

            // Wrap around
            if (nextIndex >= len) nextIndex = 0;
            if (nextIndex < 0) nextIndex = len - 1;

            // Don't loop back to where we started
            if (nextIndex === activeFieldIndex) break;

            const field = fieldsWithAck[nextIndex];
            if (!field) break;

            // Going backward: stop at every non-skip, non-derived field
            if (direction === "prev") {
              if (field.verificationPolicy !== "skip" && !field.readOnly) {
                return { fields: fieldsWithAck, activeFieldIndex: nextIndex, formReadyToSubmit: false };
              }
              continue;
            }

            // Going forward: only stop at fields needing edit
            if (needsEdit(field)) {
              return { fields: fieldsWithAck, activeFieldIndex: nextIndex, formReadyToSubmit: false };
            }
          }

          // Walked all fields, none need edit — form is ready
          if (direction === "next") {
            return { fields: fieldsWithAck, formReadyToSubmit: true };
          }
          return shouldAcknowledge ? { fields: fieldsWithAck } : {};
        }),

      setActiveField: (index) => set({ activeFieldIndex: index }),

      // E-072 — error-stop cursor hand-back. Bumping the nonce is what makes
      // FieldInput re-focus + select-all on a field that is ALREADY active;
      // the index assignment keeps the "blocker is a different field" case
      // identical to setActiveField.
      requestFieldFocus: (index) =>
        set((state) => ({
          activeFieldIndex: index,
          fieldFocusRequest: {
            index,
            nonce: (state.fieldFocusRequest?.nonce ?? 0) + 1,
          },
        })),

      setCurrentPage: (pageIndex) => set({ currentPageIndex: pageIndex }),

      setViewport: (partial) =>
        set((state) => ({
          viewport: { ...state.viewport, ...partial },
        })),

      resetViewport: () => set({ viewport: DEFAULT_VIEWPORT }),

      // R-149 — operator's preferred viewer zoom (applied on document load).
      setSavedViewerZoom: (zoom) => set({ savedViewerZoom: zoom }),

      // R-148 — bump the signal so DocumentCanvas re-runs the active fit mode.
      requestFitToWidth: () =>
        set((state) => ({ fitToWidthRequest: state.fitToWidthRequest + 1 })),

      // R-164 — Toggle fitMode AND signal a re-fit. Clearing savedViewerZoom is
      // required so the new fit isn't short-circuited by the carry-over zoom
      // path (R-149) in DocumentCanvas. The next manual wheel/double-click
      // will re-populate savedViewerZoom via useViewerZoomPersist.
      toggleFitMode: () =>
        set((state) => ({
          fitMode: state.fitMode === "width" ? "page" : "width",
          fitToWidthRequest: state.fitToWidthRequest + 1,
          savedViewerZoom: null,
          // R-199 — picking a Fit mode is an explicit intent that supersedes any
          // prior manual zoom, so clear the override and let the fit take effect.
          manualZoomActive: false,
        })),

      // R-198 — restore the persisted fit mode without bumping the re-fit signal.
      // The bitmap-load effect reads fitMode and performs the fit (R-197); a
      // direct set here avoids a redundant re-fit before the bitmap is even loaded.
      setFitMode: (mode) => set({ fitMode: mode }),

      // R-199 — set by the zoom controls (wheel / double-click / +/- buttons).
      setManualZoomActive: (active) => set({ manualZoomActive: active }),

      // R-188 — restore the operator's auto-center-on-focus preference on mount.
      setVsAutoCenterOnFocus: (enabled) => set({ vsAutoCenterOnFocus: enabled }),

      acceptDocument: () =>
        set((state) => ({
          fields: state.fields.map((f) => ({
            ...f,
            validationStatus: f.validationStatus === "pending" ? "pass" : f.validationStatus,
          })),
        })),

      rejectDocument: (_reason) =>
        set({
          // UI state reset happens after API call
        }),

      holdDocument: () =>
        set({
          // UI state reset happens after API call
        }),

      overrideField: (fieldId, reason) =>
        set((state) => ({
          fields: state.fields.map((f) =>
            f.id === fieldId
              ? {
                  ...f,
                  validationStatus: "overridden" as const,
                  isOverridden: true,
                  overrideReason: reason,
                  needsAttention: false,
                }
              : f,
          ),
        })),

      escalateField: (fieldId, reason) =>
        set((state) => ({
          fields: state.fields.map((f) =>
            f.id === fieldId
              ? {
                  ...f,
                  validationStatus: "overridden" as const,
                  isOverridden: true,
                  overrideReason: `Escalated: ${reason}`,
                  needsAttention: false,
                }
              : f,
          ),
        })),

      toggleBboxOverlays: () => set((state) => ({ showBboxOverlays: !state.showBboxOverlays })),
      toggleBboxLegend: () => set((state) => ({ showBboxLegend: !state.showBboxLegend })),
      setBboxOverlaysVisible: (visible) => set({ showBboxOverlays: visible }),
      setBboxLegendVisible: (visible) => set({ showBboxLegend: visible }),
      setSessionTimerVisible: (visible) => set({ sessionTimerVisible: visible }),

      toggleFocusMode: () => set((state) => ({ focusMode: !state.focusMode })),


      setVerifyThreshold: (threshold) =>
        set((state) => {
          // F-088 — the JT threshold usually resolves AFTER loadDocument (the JT
          // fetch is keyed off the loaded doc's jobTypeId), so the load-time
          // initial focus may have been derived from a stale/null threshold.
          // Re-derive it once the real threshold lands — but only while the doc
          // is pristine (nothing keyed or acknowledged yet), so a late fetch can
          // never yank the cursor away from a working operator. The value-change
          // guard keeps repeat set() calls (same threshold) from re-deriving.
          const pristine =
            threshold !== state.verifyThreshold &&
            state.fields.length > 0 &&
            state.fields.every((f) => !f.isDirty && !f.isAcknowledged);
          return {
            verifyThreshold: threshold,
            ...(pristine
              ? { activeFieldIndex: computeInitialFieldIndex(state.fields, threshold, state.stepReverifyPolicy) }
              : {}),
          };
        }),

      setSnippetZoom: (zoom) => set({ snippetZoom: zoom }),

      cycleSnippetZoom: () =>
        set((state) => {
          const cycle = [1, 2, 3, 0]; // 1x, 2x, 3x, auto-fit
          const currentIdx = cycle.indexOf(state.snippetZoom);
          const nextIdx = (currentIdx + 1) % cycle.length;
          return { snippetZoom: cycle[nextIdx] };
        }),

      toggleSnippetPanel: () => set((state) => ({ snippetVisible: !state.snippetVisible })),

      toggleTerminalMode: () => set((state) => ({ terminalMode: !state.terminalMode })),

      incrementSessionStat: (stat) =>
        set((state) => ({
          sessionStats: {
            ...state.sessionStats,
            [stat]: state.sessionStats[stat] + 1,
          },
        })),

      toggleGroupCollapsed: (groupId) =>
        set((state) => ({
          groups: state.groups.map((g) =>
            g.id === groupId ? { ...g, isCollapsed: !g.isCollapsed } : g,
          ),
        })),

      setPageBitmap: (pageIndex, bitmap) =>
        set((state) => ({
          pages: state.pages.map((p, i) =>
            i === pageIndex ? { ...p, imageBitmap: bitmap } : p,
          ),
        })),

      applyValidationResults: (results) =>
        set((state) => ({
          fields: state.fields.map((f) => {
            const r = results[f.id];
            if (!r) return f;
            // Never override a resolved/overridden field back to fail.
            if (f.validationStatus === "overridden") return f;
            if (r.valid) {
              return { ...f, validationStatus: "pass", validationMessage: null, needsAttention: false };
            }
            // F-023 — a failing warning-severity check is warn, not fail: it
            // stays visible (styling + needsAttention) but never blocks accept.
            return {
              ...f,
              validationStatus: r.severity === "warn" ? "warn" : "fail",
              validationMessage: r.message ?? null,
              needsAttention: true,
              ...(r.overrideBehavior !== undefined ? { overrideBehavior: r.overrideBehavior } : {}),
            };
          }),
        })),

      // S-5 Task 7 — sibling to applyValidationResults above, but driven by a
      // SINGLE WS `webhook_check_update` event rather than a batch /validate
      // response. Matches by `fieldRef` against `FieldState.name` — the
      // field's internalName identity (see WebhookCheckUpdate's doc comment,
      // state/types.ts, for why fieldRef — not a DocumentField id — is the
      // grounded key: WebhookCheckBinding.fieldRef IS the internalName, and
      // no DocumentField id exists on the wire payload at all today). A
      // 'passed' update clears the chip back to undefined, mirroring the
      // server's writeWebhookCheckState "a passed check carries no active
      // flag" convention. Deliberately does NOT touch validationStatus/
      // needsAttention — the chip is a visually independent signal from
      // field-validate fail/warn styling. No-op (never throws) when no field
      // in this pane carries that fieldRef.
      applyWebhookCheckUpdate: (update) =>
        set((state) => ({
          fields: state.fields.map((f) => {
            if (f.name !== update.fieldRef) return f;
            if (update.status === "passed") {
              return {
                ...f,
                webhookCheckStatus: undefined,
                webhookCheckMessage: null,
                webhookCheckRuleName: null,
                webhookCheckRuleId: null,
                webhookCheckOnUnresolved: undefined,
              };
            }
            return {
              ...f,
              webhookCheckStatus: update.status,
              webhookCheckMessage: update.message ?? null,
              webhookCheckRuleName: update.ruleName ?? f.webhookCheckRuleName ?? null,
              // S-5 Task 7 review round 2 — needed so FieldInput's "Retry
              // check" button can call the refire route without a separate
              // ruleId lookup.
              webhookCheckRuleId: update.ruleId,
              ...(update.onUnresolved !== undefined
                ? { webhookCheckOnUnresolved: update.onUnresolved }
                : {}),
            };
          }),
        })),

      applyRowResults: (rowResults) =>
        set((state) => {
          // The API keys rowResults by LineItemDefinition.groupId; the store
          // keys lineItems by definition id — translate once.
          const defIdByGroupId = new Map(state.lineItemDefinitions.map((d) => [d.groupId, d.id]));
          const byDefId: Record<string, RowCheckResultEntry[]> = {};
          for (const [groupId, entries] of Object.entries(rowResults)) {
            const defId = defIdByGroupId.get(groupId);
            if (defId) byDefId[defId] = entries;
          }
          // Review must-fix mirror (server sweep sibling): each apply is a FULL
          // re-statement of row-check state, not an incremental patch. First
          // sweep every stale fail/warn back to pending (a rule deleted or
          // retargeted server-side must not leave a phantom outline), then
          // stamp THIS call's per-cell winner (fail > warn > pass across rules
          // targeting the same cell).
          const lineItems: Record<string, LineItemRow[]> = {};
          for (const [defId, rows] of Object.entries(state.lineItems)) {
            const entries = byDefId[defId] ?? [];
            lineItems[defId] = rows.map((row) => {
              const rowEntries = entries.filter((e) => e.rowNumber === row.rowNumber && e.highlightColumn);
              const hasStale = Object.values(row.fields).some(
                (c) => c.validationStatus === "fail" || c.validationStatus === "warn",
              );
              if (rowEntries.length === 0 && !hasStale) return row;
              const fields = { ...row.fields };
              for (const [col, cell] of Object.entries(fields)) {
                if (cell.validationStatus === "fail" || cell.validationStatus === "warn") {
                  fields[col] = { ...cell, validationStatus: "pending" };
                }
              }
              const rank = { fail: 2, warn: 1, pass: 0 } as const;
              const winnerByColumn = new Map<string, "fail" | "warn" | "pass">();
              for (const e of rowEntries) {
                const next = e.passed ? "pass" : e.severity === "warn" ? "warn" : "fail";
                const current = winnerByColumn.get(e.highlightColumn);
                if (current === undefined || rank[next] > rank[current]) winnerByColumn.set(e.highlightColumn, next);
              }
              for (const [col, status] of winnerByColumn) {
                const cell = fields[col];
                if (cell) fields[col] = { ...cell, validationStatus: status };
              }
              return { ...row, fields };
            });
          }
          return { rowCheckResults: byDefId, lineItems };
        }),

      requestFocusRow: (groupId, rowNumber) =>
        set((state) => ({
          focusRowRequest: { groupId, rowNumber, nonce: (state.focusRowRequest?.nonce ?? 0) + 1 },
        })),

      acknowledgeWarnings: (fieldIds) =>
        set((state) => {
          const next = new Set(state.acknowledgedWarnings);
          for (const id of fieldIds) next.add(id);
          return { acknowledgedWarnings: next };
        }),

      setPendingWarnAck: (next) => set({ pendingWarnAck: next }),

      addLineItemRow: (groupId) =>
        set((state) => {
          const rows = state.lineItems[groupId] ?? [];
          const optimisticId = `pending-${crypto.randomUUID()}`;
          const nextRow: LineItemRow = {
            id: optimisticId,
            rowNumber: rows.length + 1,
            fields: {},
          };
          // Phase 20/C3b — optimistic local insert, then POST to persist. On success,
          // swap the optimistic id for the server-assigned id; on failure, roll back.
          const docId = state.documentId;
          if (docId) {
            void persistAddLineItemRow(docId, groupId, optimisticId);
          }
          return {
            lineItems: {
              ...state.lineItems,
              [groupId]: [...rows, nextRow],
            },
          };
        }),

      removeLineItemRow: (groupId, rowIndex) =>
        set((state) => {
          const rows = state.lineItems[groupId] ?? [];
          const removed = rows[rowIndex];
          const docId = state.documentId;
          if (docId && removed && !removed.id.startsWith("pending-")) {
            void persistRemoveLineItemRow(docId, removed.id);
          }
          return {
            lineItems: {
              ...state.lineItems,
              [groupId]: rows
                .filter((_, i) => i !== rowIndex)
                .map((r, i) => ({ ...r, rowNumber: i + 1 })),
            },
          };
        }),

      toggleAudio: () => set((state) => ({ audioEnabled: !state.audioEnabled })),

      setAudioVolume: (v) => set({ audioVolume: Math.max(0, Math.min(1, v)) }),

      updateLineItemCell: (groupId, rowIndex, columnName, value) =>
        set((state) => {
          const rows = state.lineItems[groupId] ?? [];
          const row = rows[rowIndex];
          if (!row) return {};
          const existingField = row.fields[columnName];
          const updatedField = {
            id: existingField?.id ?? crypto.randomUUID(),
            columnName,
            value,
            ocrValue: existingField?.ocrValue ?? null,
            ocrConfidence: existingField?.ocrConfidence ?? null,
            validationStatus: existingField?.validationStatus ?? ("pending" as const),
          };
          // Phase 20/C3b — debounced persist. Skip optimistic rows (id starts with
          // `pending-`) because their server-side id isn't known yet; the next
          // PATCH after add-row resolves will pick up the persisted value.
          const docId = state.documentId;
          if (docId && !row.id.startsWith("pending-")) {
            schedulePersistLineItemCell(docId, row.id, columnName, value);
          }
          return {
            lineItems: {
              ...state.lineItems,
              [groupId]: rows.map((r, i) =>
                i === rowIndex
                  ? { ...r, fields: { ...r.fields, [columnName]: updatedField } }
                  : r,
              ),
            },
          };
        }),

      runContentValidation: async (
        documentId: string,
        stage: string,
        opts?: { silent?: boolean },
      ): Promise<{ valid: boolean; fieldResults: Record<string, FieldValidationResult> }> => {
        // `silent` (load-time pre-validation) skips the isRevalidating toggle so the
        // RevalidationOverlay only ever flashes on the submit-gate call, never on
        // document open.
        const silent = opts?.silent ?? false;
        if (!silent) set({ isRevalidating: true });
        try {
          // Route through the shared api client so a 401 bounces to Suite SSO
          // (DH19-03b — the non-negotiable Auth UX contract). api.post throws on
          // any non-2xx, which the catch below turns into the existing fail-open.
          const result = await api.post<{
            valid: boolean;
            fieldResults: Record<string, FieldValidationResult>;
            rowResults?: Record<string, RowCheckResultEntry[]>;
          }>(
            `/documents/${documentId}/validate`,
            { stage },
          );
          get().applyValidationResults(result.fieldResults);
          // P2b — surface per-row check outcomes on the line-item grid + chip.
          get().applyRowResults(result.rowResults ?? {});
          if (!silent) set({ isRevalidating: false });
          return { valid: result.valid, fieldResults: result.fieldResults };
        } catch {
          if (!silent) set({ isRevalidating: false });
          // fail-open: never block keying on a validation transport error
          // (401 already bounced by api-client)
          return { valid: true, fieldResults: {} };
        }
      },
    };
  });
}

// Default instance — the standalone VS keeps consuming THIS, so single-doc
// behavior is byte-identical (the pin). The station creates fresh instances
// per pane via `createVerificationStore()` + `VerificationStoreProvider`.
export const useVerificationStore = createVerificationStore();

/**
 * E-050 — the ONE batch-identity selector for VS surfaces (header, cross-batch
 * transition, accept toast, batch tree drawer, terminal stats bar, detached
 * panel). Applies the shared `preferredBatchLabel` precedence
 * (customDisplayLabel ?? displayId ?? name) so the filename-derived free-text
 * `name` is the last resort, never the identity. Works on any store snapshot
 * (default instance or pane store) — pass it straight to
 * `useVerificationStore(selectBatchLabel)` / `usePaneStore(selectBatchLabel)`.
 */
export function selectBatchLabel(
  s: Pick<VerificationState, "batchName"> &
    Partial<Pick<VerificationState, "batchDisplayId" | "batchCustomDisplayLabel">>,
): string {
  return preferredBatchLabel({
    customDisplayLabel: s.batchCustomDisplayLabel ?? null,
    displayId: s.batchDisplayId ?? null,
    name: s.batchName,
  });
}

/**
 * @deprecated SP3 — prefer `store.getState().flushPendingWrites()`. Retained
 *  so the standalone VS's imperative callers (VerificationStation) keep working
 *  against the DEFAULT instance. Flushes the default instance's pending maps —
 *  exactly what the module-global flush did before the factory refactor.
 */
export function flushPendingFieldWrites(): Promise<void> {
  return useVerificationStore.getState().flushPendingWrites();
}
